import {useTheme} from '@mui/material';
import React, {Fragment} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import {getFeatureFlagValue} from 'infra/http/utils/get-feature-flag-value';
import {HighlightedText, IconIllustration, IFrameModal} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {ModalError} from 'presentation/components/ModalError';
import {
	ExamBookingEnum,
	FeaturesEnum,
	HealthBookingEnum,
	OdontoBookingEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';
import {useGetEndpointUrl} from 'presentation/hooks';
import {useServicePermission} from 'presentation/hooks/useServicePermission';
import Icon from 'presentation/icons';
import {useOverbookingModalStore} from 'presentation/pages/private/booking/components/Overbooking/store';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';

import {useBookingConfirmationDrawer} from 'presentation/pages/private/booking/hooks/useBookingConfirmationDrawer';
import {useExternalServiceStore} from 'presentation/pages/private/store/externalSerivceUrlStore';
import {translations} from 'presentation/translations-new';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useIFrameModalStore} from '../../store/iFrameModalStore';
import {
	StyledCard,
	StyledCardActionArea,
	StyledIcon,
	StyledIconButtonContainer,
	StyledImageIcon,
	StyledStack,
} from './styles';
import {ServiceCardProps} from './types';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {
	title: modalErrorTitle,
	subtitle,
	description,
	tryAgainBtn,
	backAction,
} = translations['pt-br'].others.genericErrorModal;

export function ServiceCard({
	route,
	icon,
	imageSrc,
	title,
	type,
	endpoint,
	disableCache,
	openInIframe,
	externalLink,
	featureFlagName,
	routeReplace = true,
	isBlob,
	dataTagId,
	handleClick,
}: ServiceCardProps) {
	const {canOpenService} = useServicePermission();
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {getLink, isLoading, isError, setIsError} = useGetEndpointUrl(title);
	const cachedURL = useExternalServiceStore(state => state.urls[title]);
	const theme = useTheme();
	const [searchParams] = useSearchParams();
	const query = searchParams.get('q') || '';
	const iFrameOpenState = useIFrameModalStore(state => state.isIFrameOpen);
	const setIFrameOpenState = useIFrameModalStore(state => state.setIFrameOpen);
	const setOverbookingModalOpen = useOverbookingModalStore(
		state => state.setOverbookingModalOpen,
	);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {setSelectedServiceType, handleOpenBookingConfirmationDrawer} =
		useBookingConfirmationDrawer();
	const {isMobile} = useMediaQueries();
	const {isEnabled: isAccordionServicesFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ACCORDION_SERVICES,
	);

	const shouldOpenIframe = Boolean(openInIframe) && iFrameOpenState && Boolean(cachedURL);

	async function handleClickService() {
		const canAcessService = canOpenService(type);

		if (!canAcessService) return;

		if (featureFlagName) {
			const isAccordionServicesFeatureFlagEnabled =
				await getFeatureFlagValue(featureFlagName);

			if (endpoint && !isAccordionServicesFeatureFlagEnabled) {
				getLink(endpoint, false, isBlob);
				return;
			}

			if (route) {
				navigate(route, {
					state: pathname,
					replace: routeReplace,
				});
				return;
			}
		}

		if (handleClick) {
			handleClick();
			return;
		}

		if (externalLink) {
			window.open(externalLink, '_blank', 'noopener,noreferrer');
			return;
		}

		if (openInIframe && cachedURL) {
			setIFrameOpenState(true);
			return;
		}

		if (openInIframe && endpoint) {
			getLink(endpoint, openInIframe);
			return;
		}

		if (!disableCache && cachedURL) {
			window.open(cachedURL);
			return;
		}

		if (endpoint) {
			getLink(endpoint, false, isBlob);
			return;
		}

		if (route) {
			if (route === HealthBookingEnum.HEALTH_BOOKING_FLOW) {
				setOverbookingModalOpen(true);
				setBookingData(prev => ({
					...prev,
					bookingOrigin: BookingOriginEnum.BOOKING_HEALTH,
				}));
				return;
			}

			if (route === ExamBookingEnum.EXAM_BOOKING_FLOW) {
				setSelectedServiceType(BookingTypeEnum.EXAM);
				handleOpenBookingConfirmationDrawer();
				return;
			}

			if (route === OdontoBookingEnum.ODONTO_BOOKING_FLOW) {
				setSelectedServiceType(BookingTypeEnum.ODONTO);
				handleOpenBookingConfirmationDrawer();
				return;
			}

			if (route === TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW) {
				setSelectedServiceType(BookingTypeEnum.TELEHEALTH);
				handleOpenBookingConfirmationDrawer();
				return;
			}

			navigate(route, {
				state: pathname,
				replace: routeReplace,
			});
		}
	}

	function getButtonIcon() {
		if (route || openInIframe || handleClick) {
			return isMobile && isAccordionServicesFeatureFlagEnabled
				? 'chevron-right-outlined'
				: 'arrow-right-filled';
		}

		return 'ben-export';
	}

	return (
		<Fragment>
			<StyledCardActionArea
				disabled={isLoading}
				onClick={handleClickService}
				data-tag-id={dataTagId}>
				<StyledCard variant="outlined" isEnabled={isAccordionServicesFeatureFlagEnabled}>
					<StyledStack isEnabled={isAccordionServicesFeatureFlagEnabled}>
						{imageSrc ? <StyledImageIcon src={imageSrc} alt="" /> : null}
						{!imageSrc && icon && isMobile && isAccordionServicesFeatureFlagEnabled ? (
							<IconIllustration
								customIconName={icon}
								variant="primary"
								size="smaller"
							/>
						) : null}
						{!imageSrc && icon && !(isMobile && isAccordionServicesFeatureFlagEnabled) ? (
							<StyledIcon name={icon} size={24} />
						) : null}
						<HighlightedText text={title} highlight={query} />
					</StyledStack>
					<StyledIconButtonContainer>
						{isLoading ? (
							<Loading size={20} color="inherit" />
						) : (
							<Icon
								name={getButtonIcon()}
								size={20}
								color={theme.palette.primary.main}
							/>
						)}
					</StyledIconButtonContainer>
				</StyledCard>
			</StyledCardActionArea>

			{isError ? (
				<ModalError
					open={isError}
					onClose={() => setIsError(false)}
					headerTitle={modalErrorTitle}
					title={subtitle}
					isFullScreen={false}
					maxWidth={420}
					description={description}
					firstButton={{
						title: backAction,
						handleClick: () => setIsError(false),
						variant: 'outlined',
					}}
					secondButton={{
						title: tryAgainBtn,
						handleClick: () => {
							setIsError(false);
							handleClickService();
						},
						variant: 'contained',
					}}
				/>
			) : null}

			<IFrameModal
				openIframe={shouldOpenIframe}
				setOpenIframe={setIFrameOpenState}
				title={title}
				url={cachedURL}
				handleBack={() => setIFrameOpenState(false)}
			/>
		</Fragment>
	);
}
